import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../util/errors';
import * as log from '../util/log';
import { createImageVariantConfig } from '../util/sdkLoader';
import { addMarketplaceEntities } from './marketplaceData.duck';

/**
 * "You may also like" on the listing page: other pieces from the same category.
 *
 * This lives outside ListingPage.duck because it is fetched after mount rather
 * than in loadData — the row sits below the fold and should never hold up the
 * listing itself.
 */

// The row shows four pieces. One extra is queried so that dropping the listing
// being viewed does not leave a gap. View All is shown when the category has
// more than these four (plus the listing being viewed).
const RESULT_COUNT = 4;

const fetchSimilarListingsPayloadCreator = (arg, thunkAPI) => {
  const { extra: sdk, dispatch, rejectWithValue } = thunkAPI;
  const { listingId, queryParams, listingImageConfig } = arg;
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    listingImageConfig || {};
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      ...queryParams,
      page: 1,
      perPage: RESULT_COUNT + 1,
      include: ['images', 'author'],
      // The listing being viewed can come back in these results, and image
      // entities merge by replacing `variants` wholesale — so this query has to
      // ask for the gallery's scaled variants too or it strips them from the
      // listing whose page we are on.
      'fields.image': [
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.scaled-large',
        'variants.scaled-xlarge',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    })
    .then(response => {
      // This listing is often in the same category query. Merging it in with
      // limit.images=1 would replace its gallery with a single thumbnail.
      const listings = (response.data.data || []).filter(l => l.id.uuid !== listingId);
      dispatch(
        addMarketplaceEntities({
          ...response,
          data: {
            ...response.data,
            data: listings,
          },
        })
      );
      const listingIds = listings.map(l => l.id).slice(0, RESULT_COUNT);
      // Category query includes the listing being viewed, so more than four
      // similar pieces means more than five results in total.
      const totalItems = response.data.meta?.totalItems ?? 0;
      const hasMore = totalItems > RESULT_COUNT + 1;
      return { listingIds, hasMore };
    })
    .catch(e => {
      log.error(e, 'similar-listings-fetch-failed');
      return rejectWithValue(storableError(e));
    });
};

export const fetchSimilarListings = createAsyncThunk(
  'similarListings/fetchSimilarListings',
  fetchSimilarListingsPayloadCreator
);

const initialState = {
  forListingId: null,
  listingIds: [],
  hasMore: false,
  inProgress: false,
  error: null,
};

const similarListingsSlice = createSlice({
  name: 'similarListings',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchSimilarListings.pending, (state, action) => {
        // Results are keyed by the listing they belong to, so navigating between
        // listings never shows the previous listing's row.
        state.forListingId = action.meta.arg.listingId;
        state.listingIds = [];
        state.hasMore = false;
        state.inProgress = true;
        state.error = null;
      })
      .addCase(fetchSimilarListings.fulfilled, (state, action) => {
        state.forListingId = action.meta.arg.listingId;
        state.listingIds = action.payload.listingIds;
        state.hasMore = !!action.payload.hasMore;
        state.inProgress = false;
      })
      .addCase(fetchSimilarListings.rejected, (state, action) => {
        state.forListingId = action.meta.arg.listingId;
        state.listingIds = [];
        state.hasMore = false;
        state.inProgress = false;
        state.error = action.payload;
      });
  },
});

export default similarListingsSlice.reducer;
